import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import AdvertiserPanel from "./AdvertiserPanel";
import MessageHeading from "./CommonComponents/MessageHeading";
import MessageBox from "../../components/MessageBox";
import { NavLink } from "react-router-dom";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ResponseLoader from "../../components/Loader/ResponseLoader";
import { FaEllipsisVertical, FaEye } from "react-icons/fa6";
import { CiShare1 } from "react-icons/ci";
import { IconDeleteFigma, IconEditFigma } from "../../assets/MyIcons";

const SSAICampaignList = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [fastChannels, setFastChannels] = useState([]);
    const [loading, setLoading] = useState(false);

    // Assignment Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useState(null);
    const [selectedChannelId, setSelectedChannelId] = useState("");

    const SSAI_API_URL = process.env.REACT_APP_SSAI_API_URL;

    const fetchCampaigns = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${SSAI_API_URL}/api/advertiser/campaigns`, {
                headers: { 'x-api-key': process.env.REACT_APP_SSAI_API_KEY || 'ssai_secure_key_2024' }
            });
            if (res.data && res.data.success) {
                setCampaigns(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching SSAI Campaigns:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchChannels = async () => {
        try {
            const res = await axios.get(`${SSAI_API_URL}/api/advertiser/channel-list`, {
                headers: { 'x-api-key': process.env.REACT_APP_SSAI_API_KEY || 'ssai_secure_key_2024' }
            });
            if (res.data && res.data.success) {
                setFastChannels(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching channels:", error);
            toast.error("Could not load FAST channels.");
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this live SSAI campaign?")) return;
        setLoading(true);
        try {
            await axios.delete(`${SSAI_API_URL}/api/advertiser/campaigns/${id}`, {
                headers: { 'x-api-key': process.env.REACT_APP_SSAI_API_KEY || 'ssai_secure_key_2024' }
            });
            toast.success("Campaign deleted successfully");
            fetchCampaigns();
        } catch (error) {
            toast.error("Failed to delete campaign");
            setLoading(false);
        }
    };

    const handleAssignClick = (campaignId) => {
        setSelectedCampaignId(campaignId);
        setSelectedChannelId("");
        fetchChannels();
        setShowModal(true);
    };

    const handleSaveAssignment = async () => {
        if (!selectedChannelId) {
            toast.warning("Please select a valid FAST Channel.");
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${SSAI_API_URL}/api/advertiser/channels/${selectedChannelId}/assign`, {
                campaignId: selectedCampaignId
            }, {
                headers: { 'x-api-key': process.env.REACT_APP_SSAI_API_KEY || 'ssai_secure_key_2024' }
            });
            toast.success("Campaign assigned to channel!");
            setShowModal(false);
        } catch (error) {
            toast.error("Failed to assign campaign.");
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'active': return 'bg-success';
            case 'paused': return 'bg-warning';
            case 'completed': return 'bg-info';
            case 'draft': return 'bg-secondary';
            default: return 'bg-primary';
        }
    };

    return (
        <AdvertiserPanel>
            <div className="p-3 rounded-3">
                {loading && <ResponseLoader />}
                <div className="theme-bg-color p-3 mt-3 rounded-2 bootstrapTable multi-delete-width">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <MessageHeading text={"SSAI Ad Management"} className="advertiserHeading" />
                        <NavLink to="/ssai-campaign-create" className="px-4 py-2 theme-button-bg-color theme-button-text-color admin-button-text rounded-3 text-decoration-none d-inline-flex align-items-center">
                            + <span className="ms-2">Create Campaign</span>
                        </NavLink>
                    </div>

                    <div className="table-responsive rounded-3">
                        <table className="table table-hover align-middle mb-0 no-border-table theme-text-color">
                            <thead>
                                <tr>
                                    <th className="ps-3 theme-text-color admin-input-title">Campaign</th>
                                    <th className="theme-text-color admin-input-title">Status</th>
                                    <th className="theme-text-color admin-input-title">Type</th>
                                    <th className="theme-text-color admin-input-title">Budget</th>
                                    <th className="theme-text-color admin-input-title">Impressions</th>
                                    <th className="text-end pe-3 theme-text-color admin-input-title">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.length > 0 ? campaigns.map((item, index) => (
                                    <tr key={index}>
                                        <td className="ps-3">
                                            <div className="fw-bold theme-text-color table-heading d-flex align-items-center">
                                                <span>{item.name}</span>
                                                <CiShare1 style={{ width: "15px", height: "15px", strokeWidth: "1px", marginLeft: "6px" }} />
                                            </div>
                                            <small className="theme-text-color opacity-75 table-subheading">{item.id.substring(0, 8)}</small>
                                        </td>
                                        <td>
                                            <span className={`badge ${getStatusBadgeClass(item.status)} text-uppercase`}>
                                                {item.status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="small text-muted">{item.adType}</div>
                                            <div className="small">{item.type}</div>
                                        </td>
                                        <td>
                                            {item.budget ? `$${item.budget}` : <span className="text-muted">No Limit</span>}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center theme-text-color">
                                                <FaEye className="me-2" />
                                                {item.stats?.impressions || 0}
                                            </div>
                                        </td>
                                        <td className="text-end pe-3">
                                            <div className="editdropdown">
                                                <span className="editdropdown-button">
                                                    <FaEllipsisVertical className="theme-text-color" />
                                                </span>
                                                <div className="editdropdown-menu theme-bg-color rounded-2 overflow-hidden">
                                                    <NavLink to={`/ssai-campaign-edit/${item.id}`} className="theme-text-color theme-bg-color">
                                                        <IconEditFigma styled={{ width: "20px", height: "20px" }} />
                                                        <span className="theme-text-color">
                                                            <MessageBox text="Edit" classname="ms-2 theme-text-color" />
                                                        </span>
                                                    </NavLink>

                                                    <div onClick={() => handleAssignClick(item.id)} className="commonActionPadding theme-bg-color">
                                                        <FaEye style={{ width: "18px", height: "18px" }} />
                                                        <MessageBox text="Assign to Channel" classname="ms-2 theme-text-color" />
                                                    </div>

                                                    <div onClick={() => handleDelete(item.id)} className="commonActionPadding theme-bg-color">
                                                        <IconDeleteFigma styled={{ width: "20px", height: "20px" }} />
                                                        <MessageBox text="Delete" classname="ms-2 theme-text-color" />
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="6" className="py-5 text-center text-muted">
                                            <i className="bi bi-inbox fs-2 d-block mb-2"></i>
                                            No SSAI Campaigns found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Assign to FAST Channel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-muted mb-3">Assign this campaign to a live 24/7 FAST channel. The ad will be injected during SCTE-35 split points.</p>
                    <select
                        className="form-select form-select-lg"
                        value={selectedChannelId}
                        onChange={(e) => setSelectedChannelId(e.target.value)}
                    >
                        <option value="">-- Select Channel --</option>
                        {fastChannels.map(ch => (
                            <option key={ch.channelId} value={ch.channelId}>
                                {ch.name} ({ch.status})
                            </option>
                        ))}
                    </select>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button variant="primary" className="theme-button-bg-color border-0" onClick={handleSaveAssignment}>Confirm Assignment</Button>
                </Modal.Footer>
            </Modal>
        </AdvertiserPanel>
    );
};

export default SSAICampaignList;
